/**
 * oj-quick-view.js — Quick View / Quick Shop on collection cards.
 *
 * <oj-quick-view-trigger>
 *   A quiet trigger that sits beside a product card. On click it fetches the
 *   compact product section via the Section Rendering API
 *   ({product.url}?section_id=oj-quick-view) and injects it into a single
 *   shared <oj-quick-view-dialog>, appended to <body> on first use.
 *
 * <oj-quick-view-dialog>
 *   Extends Horizon's DialogComponent (scroll-lock, focus trap, Esc handled by
 *   the base class). Adds: close-button + backdrop wired via direct
 *   addEventListener (on:click on innerHTML-injected nodes is unreliable),
 *   auto-close on cart add, and a server-rendered price morph on variant change
 *   (re-fetch the section, morph only the price node — money is never computed
 *   in JS). Reduced-motion is honoured in CSS (oj-quick-view.css); JS adds no
 *   motion of its own.
 *
 * Commerce (add-to-cart, variant picker, price) is Horizon's own plumbing —
 * product-form.js / variant-picker.js / product-price.js hydrate the injected
 * markup because they are already loaded globally.
 */

import { morph } from '@theme/morph';
import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

const SECTION_ID = 'oj-quick-view';
const DIALOG_ID = 'oj-quick-view-dialog';

/**
 * Returns the shared dialog, creating and appending it to <body> on first use.
 * @returns {OjQuickViewDialog}
 */
function getSharedDialog() {
  let dialog = /** @type {OjQuickViewDialog | null} */ (document.getElementById(DIALOG_ID));
  if (dialog) return dialog;

  dialog = /** @type {OjQuickViewDialog} */ (document.createElement('oj-quick-view-dialog'));
  dialog.id = DIALOG_ID;
  dialog.setAttribute('data-oj-quick-view-dialog', '');
  dialog.innerHTML = `
    <dialog
      ref="dialog"
      class="oj-quick-view-dialog"
      aria-label="Quick view"
      data-oj-quick-view-chrome
    >
      <div class="oj-quick-view-dialog__panel">
        <button
          type="button"
          class="oj-quick-view-dialog__close"
          data-oj-quick-view-close
          aria-label="Close"
        >
          <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">
            <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" stroke-width="1.25" fill="none" stroke-linecap="round"/>
          </svg>
        </button>
        <div class="oj-quick-view-dialog__content" data-oj-quick-view-content aria-live="polite">
          <div class="oj-quick-view-dialog__loading" data-oj-quick-view-loading aria-hidden="true"></div>
        </div>
      </div>
    </dialog>
  `;
  document.body.appendChild(dialog);
  return dialog;
}

/**
 * Builds the Section Rendering API URL for a product's quick view.
 * @param {string} productUrl - The product page URL.
 * @param {string} [variantId] - Optional variant id to render.
 * @returns {string}
 */
function buildSectionUrl(productUrl, variantId) {
  const base = productUrl.split('?')[0];
  let url = `${base}?section_id=${SECTION_ID}`;
  if (variantId) url += `&variant=${encodeURIComponent(variantId)}`;
  return url;
}

class OjQuickViewTrigger extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const button = this.querySelector('button');
    button?.addEventListener('click', this.#handleClick, { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  get productUrl() {
    return this.getAttribute('data-product-url') || '';
  }

  #handleClick = async (/** @type {MouseEvent} */ event) => {
    event.preventDefault();
    const productUrl = this.productUrl;
    if (!productUrl) return;

    const dialog = getSharedDialog();
    await dialog.openWithProduct(productUrl);
  };
}

if (!customElements.get('oj-quick-view-trigger')) {
  customElements.define('oj-quick-view-trigger', OjQuickViewTrigger);
}

class OjQuickViewDialog extends DialogComponent {
  /** @type {AbortController | null} */
  #localAbort = null;
  /** @type {AbortController | null} */
  #fetchAbort = null;
  /** @type {string} */
  #productUrl = '';

  connectedCallback() {
    super.connectedCallback();

    this.#localAbort = new AbortController();
    const { signal } = this.#localAbort;

    // Close button + backdrop are wired directly, not via on:click — the chrome
    // is injected as innerHTML and on:click delegation is unreliable on it.
    const closeButton = this.querySelector('[data-oj-quick-view-close]');
    closeButton?.addEventListener('click', this.#close, { signal });

    const dialogEl = this.refs.dialog;
    dialogEl?.addEventListener('click', this.#handleBackdropClick, { signal });

    // Auto-close once the item is added to the cart (success only).
    this.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });

    // Re-fetch + morph the price (server-rendered) whenever the variant changes.
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#localAbort?.abort();
    this.#localAbort = null;
    this.#fetchAbort?.abort();
    this.#fetchAbort = null;
  }

  get #content() {
    return this.querySelector('[data-oj-quick-view-content]');
  }

  /**
   * Fetches the compact section for a product and opens the dialog.
   * @param {string} productUrl - The product page URL.
   */
  async openWithProduct(productUrl) {
    this.#productUrl = productUrl;
    this.setAttribute('data-loading', '');
    this.showDialog();

    const html = await this.#fetchSection(buildSectionUrl(productUrl));
    this.removeAttribute('data-loading');
    if (!html) return;

    const section = html.querySelector('[data-oj-quick-view-section]');
    const content = this.#content;
    if (section && content) {
      content.replaceChildren(section);
    }
  }

  /**
   * @param {string} url
   * @returns {Promise<Document | null>}
   */
  async #fetchSection(url) {
    this.#fetchAbort?.abort();
    this.#fetchAbort = new AbortController();
    try {
      const response = await fetch(url, { signal: this.#fetchAbort.signal });
      if (!response.ok) throw new Error(`Quick view fetch failed: ${response.status}`);
      const text = await response.text();
      return new DOMParser().parseFromString(text, 'text/html');
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return null;
      console.error(error);
      return null;
    } finally {
      this.#fetchAbort = null;
    }
  }

  #close = () => {
    this.closeDialog();
  };

  /** @param {MouseEvent} event */
  #handleBackdropClick = (event) => {
    // The native <dialog> backdrop is the dialog element itself; a click whose
    // target is the dialog (not the panel inside) is a backdrop click.
    if (event.target === this.refs.dialog) {
      this.closeDialog();
    }
  };

  /** @param {CustomEvent} event */
  #handleCartUpdate = (event) => {
    if (event.detail?.data?.didError) return;
    this.closeDialog();
  };

  /** @param {CustomEvent} event */
  #handleVariantUpdate = async (event) => {
    const variantId = event.detail?.resource?.id ?? event.detail?.variant?.id;
    if (!variantId || !this.#productUrl) return;

    const html = await this.#fetchSection(buildSectionUrl(this.#productUrl, String(variantId)));
    if (!html) return;

    const newPrice = html.querySelector('[data-oj-quick-view-price]');
    const currentPrice = this.querySelector('[data-oj-quick-view-price]');
    if (newPrice && currentPrice) {
      morph(currentPrice, newPrice);
    }

    // Swap the media to the selected colour's shot (server-rendered variant.featured_media).
    const newMedia = html.querySelector('.oj-quick-view__media');
    const currentMedia = this.querySelector('.oj-quick-view__media');
    if (newMedia && currentMedia) {
      morph(currentMedia, newMedia);
    }
  };
}

if (!customElements.get('oj-quick-view-dialog')) {
  customElements.define('oj-quick-view-dialog', OjQuickViewDialog);
}
